/**
 * Copyright (c) 2023-present.
 * This source code is copied from https://github.com/justinmahar/react-social-media-embed
 */
import classNames from 'classnames'
import React, { useEffect, useState, useRef, useMemo } from 'react'
import { Subs } from 'react-sub-unsub'
import { useFrame } from '../hooks/useFrame.js'
import { SkeletonBox, Box } from 'chemistry'
import { EmbedStyle } from './embedStyle.js'

const borderRadius = 3

// Embed Stages
const CHECK_SCRIPT_STAGE = 'check-script'
const CONFIRM_SCRIPT_LOADED_STAGE = 'confirm-script-loaded'
const PROCESS_EMBED_STAGE = 'process-embed'
const CONFIRM_EMBED_SUCCESS_STAGE = 'confirm-embed-success'
const RETRYING_STAGE = 'retrying'
const EMBED_SUCCESS_STAGE = 'embed-success'

export default function InstagramEmbed({
  url,
  height,
  width = 540,
  placeholderDisabled = false,
  scriptLoadDisabled = false,
  retryDelay = 5000,
  retryDisabled = false,
  igVersion = '14',
  frame = undefined,
  ...divProps
}) {
  const embedId = url.match(/(.*)(\/p\/)(.*)(\/)/)[3] || ''
  const idRef = useRef(`ig-${embedId}`)
  const [stage, setStage] = useState(CHECK_SCRIPT_STAGE)
  const embedSuccess = useMemo(() => stage === EMBED_SUCCESS_STAGE, [stage])
  const [processTime, setProcessTime] = useState(Date.now())
  const embedContainerKey = useMemo(() => `${idRef.current}-${processTime}`, [processTime])
  const frm = useFrame(frame)

  // === === === === === === === === === === === === === === === === === === ===
  // Embed Stages
  // === === === === === === === === === === === === === === === === === === ===

  // Check Script Stage
  useEffect(() => {
    if (stage === CHECK_SCRIPT_STAGE) {
      if (frm.window?.instgrm?.Embeds?.process) {
        setStage(PROCESS_EMBED_STAGE)
      } else if (!scriptLoadDisabled) {
        setStage(CONFIRM_SCRIPT_LOADED_STAGE)
      } else {
        console.error('Instagram embed script not found. Unable to process Instagram embed:', url)
      }
    }
  }, [scriptLoadDisabled, stage, url, frm.window])

  // Confirm Script Loaded Stage
  useEffect(() => {
    const subs = new Subs()
    if (stage === CONFIRM_SCRIPT_LOADED_STAGE) {
      subs.setInterval(() => {
        if (frm.window?.instgrm?.Embeds?.process) {
          setStage(PROCESS_EMBED_STAGE)
        }
      }, 1)
    }
    return subs.createCleanup()
  }, [stage, frm.window])

  // Process Embed Stage
  useEffect(() => {
    if (stage === PROCESS_EMBED_STAGE) {
      const process = frm.window?.instgrm?.Embeds?.process
      if (process) {
        process()
        setStage(CONFIRM_EMBED_SUCCESS_STAGE)
      } else {
        console.error('Instagram embed script not found. Unable to process Instagram embed:', url)
      }
    }
  }, [stage, frm.window, url])

  // Confirm Embed Success Stage
  useEffect(() => {
    const subs = new Subs()
    if (stage === CONFIRM_EMBED_SUCCESS_STAGE) {
      subs.setInterval(() => {
        if (frm.document) {
          const preEmbedElement = frm.document.getElementById(idRef.current)
          if (!preEmbedElement) {
            setStage(EMBED_SUCCESS_STAGE)
          }
        }
      }, 1)
      if (!retryDisabled) {
        subs.setTimeout(() => {
          setStage(RETRYING_STAGE)
        }, retryDelay)
      }
    }
    return subs.createCleanup()
  }, [retryDelay, retryDisabled, stage, frm.document])

  // Retrying Stage
  useEffect(() => {
    if (stage === RETRYING_STAGE) {
      // This forces the embed container to remount
      setProcessTime(Date.now())
      setStage(PROCESS_EMBED_STAGE)
    }
  }, [stage])

  // END Embed Stages
  // === === === === === === === === === === === === === === === === === === ===

  const urlWithNoQuery = url.replace(/[?].*$/, '')
  const cleanUrlWithEndingSlash = `${urlWithNoQuery}${urlWithNoQuery.endsWith('/') ? '' : '/'}`

  return (
    <Box
      {...divProps}
      className={classNames('rsme-embed rsme-instagram-embed', idRef.current, divProps.className)}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: `${width}px`,
        m: '0 auto',
        height: height ? `${height}px` : '100%',
        borderRadius,
        ...divProps.style
      }}
    >
      <EmbedStyle />
      <Box
        as='blockquote'
        key={embedContainerKey}
        className='instagram-media'
        data-instgrm-permalink={`${cleanUrlWithEndingSlash}?utm_source=ig_embed&utm_campaign=loading`}
        data-instgrm-version={igVersion}
        data-instgrm-captioned
        sx={{
          width: 'calc(100% - 2px)',
          margin: '0',
          minWidth: '200px!important'
        }}
      >
        <Box id={idRef.current} className='instagram-media-pre-embed rsme-d-none'>
          &nbsp;
        </Box>
        {!embedSuccess && !placeholderDisabled && <SkeletonBox url={url} />}
      </Box>
    </Box>
  )
}
